import { createAuthGuard } from '@auth0/auth0-vue';
import { createRouter, createWebHistory } from '@ionic/vue-router';
import { START_LOCATION } from 'vue-router';
import { applyScaleForRoute } from '@/composables/useFontScale';
import { isPlatform, toastController } from '@ionic/vue';
import { auth0 } from '@/utils/auth0';
import { isNativeNotWeb } from '@/utils/platform-check';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { Y21_FLAG } from '@/constants/y21';
import {
  unlockOrientation,
  lockOrientation,
  getOrientationActionForRoute,
} from '@/utils/screen-orientation';
import store from '@/store';
import mixpanelInstance, {
  shouldBlockTracking,
  stopMixpanelRecording,
  startMixpanelRecording,
} from '@/utils/mixpanel';
import { trackPageView } from '@/utils/analytics';
import { handleRouteChangeForSessionReplay } from '@/utils/datadog';
import { getStoryblokUrlParams } from '@/utils/storyblok-visual-editor';
import { markTabRouteReady } from '@/utils/tab-transition-metrics';
import { createEndPinSessionOnHome } from '@/utils/giving-pin-session';
import TabsPage from '../pages/tabs/TabsPage.vue';
import BirthdayBoardFavouriteMessages from '../pages/birthday-board/favourite-messages.vue';
import BirthdayBoardHiddenMessages from '../pages/birthday-board/hidden-messages.vue';
import BirthdayBoard from '../pages/birthday-board/index.vue';
import BirthdayBoardMessage from '../pages/birthday-board/message.vue';
import BirthdayBoardMyMessages from '../pages/birthday-board/my-messages.vue';
import BirthdayBoardPinnedMessages from '../pages/birthday-board/pinned-messages.vue';
import Calendar from '../pages/calendar/index.vue';
import ChangePassword from '../pages/change-password/index.vue';
import ChurchUpdatesReadMore from '../pages/church-updates/_read-more.vue';
import ChurchUpdates from '../pages/church-updates/index.vue';
import EInvitesPage from '../pages/e-invites/index.vue';
import EventPage from '../pages/event-page/_slug.vue';
import Forms from '../pages/forms/_formName.vue';
import FD2013 from '../pages/hogcfd/2013.vue';
import FD2014 from '../pages/hogcfd/2014.vue';
import FD2015 from '../pages/hogcfd/2015.vue';
import FD2016 from '../pages/hogcfd/2016.vue';
import FD2017 from '../pages/hogcfd/2017.vue';
import FD2024 from '../pages/hogcfd/2024.vue';
import FathersDay from '../pages/hogcfd/index.vue';
import MD2023 from '../pages/hogcmd/md2023.vue';
import MD20241 from '../pages/hogcmd/md2024-1.vue';
import MD20242 from '../pages/hogcmd/md2024-2.vue';
import MD20243 from '../pages/hogcmd/md2024-3.vue';
import MD20244 from '../pages/hogcmd/md2024-4.vue';
import MD20245 from '../pages/hogcmd/md2024-5.vue';
import HomepageDebug from '../pages/homepage/debug.vue';
import PreLoadingPage from '../pages/homepage/pre-loading.vue';
import Sermon from '../pages/library/_slug.vue';
import SearchPage from '../pages/library/components/SearchSection.vue';
import Login from '../pages/login/index.vue';
import Milestones from '../pages/milestones/index.vue';
import { createMyGivingRoutes } from './mygiving-routes';
import MyQR from '../pages/my-qr/index.vue';
import Photobooth from '../pages/photobooth/index.vue';
import Photodrop from '../pages/photodrop/index.vue';
import PhotodropUploadTest from '../pages/photodrop/upload-test.vue';
import PhotographerApp from '../pages/photographer-app/index.vue';
import ReceivePhotos from '../pages/receive-photos/index.vue';
import PreSignIn from '../pages/pre-sign-in.vue';
import Profile from '../pages/profile/index.vue';
import ProtectedPage from '../pages/protected/_name.vue';
import ServiceHighlight from '../pages/service-highlights/index.vue';
import ServiceReplayDetail from '../pages/service-replay/_id.vue';
import SsoApprove from '../pages/sso/approve.vue';
import {
  TRAINING_GATEWAY_PATH,
  TRAINING_SIGNED_OUT_PATH,
  keepOnTrainingGateway,
  trainingCallbackRedirect,
} from '@/utils/training-gateway';
import SsoConnectedApps from '../pages/sso/connected-apps.vue';
import SuccessLaunchPage from '../pages/success-launch.vue';
import UpdateDetails from '../pages/updates/_id.vue';
import Updates from '../pages/updates/index.vue';
import Onboarding from '../pages/register/index.vue';
import Registration from '../pages/registration/index.vue';
import ForgotEmail from '../pages/forgot-email/index.vue';
import ForgotEmailReset from '../pages/forgot-email/reset.vue';
import ForgotEmailContactUs from '../pages/forgot-email/contact-us.vue';
import ForgotPassword from '../pages/forgot-password/index.vue';
import YoutubeProxy from '../pages/youtube.vue';
import EasterEggPage from '../pages/easter-egg/index.vue';
import Scanner from '../pages/scanner/index.vue';
import { scannerFlagFor } from '@/utils/scanner-access';
import CourseRedeem from '../pages/courses/redeem.vue';
import CourseLeaderUnlock from '../pages/courses/unlock.vue';
import Vouchers from '../pages/vouchers/index.vue';
import VoucherDetail from '../pages/vouchers/_id.vue';
import AbsolutelyEntry from '../pages/absolutely/index.vue';
import AbsolutelyOnboarding from '../pages/absolutely/onboarding.vue';
import AbsolutelyHome from '../pages/absolutely/home.vue';
import AbsolutelyUnlock from '../pages/absolutely/unlock.vue';
import ChapterIndex from '../pages/absolutely/chapter/index.vue';
// Courses
import CoursesIndex from '../pages/courses/index.vue';
import { createCoursesPastoralGuard } from './guards/courses-pastoral-guard';
import { createCoursesGuard } from './guards/courses-guard';
import { createLeaderResourceGuard } from './guards/leader-resource-guard';
import { createCoursesNotesWebGuard } from './guards/courses-notes-web-guard';
import { isNativeNotesPlatform } from '@/utils/courses-notes-availability';
import { NEXT_STEPS_CHOOSER_PATH } from '@/pages/courses/notes-hub/back-href';
import { createTokenPrimingGuard } from './guards/token-priming-guard';
import { createMyGivingLinkGuard } from './guards/mygiving-link-guard';
import { ensureAccessToken } from './ensure-access-token';
import { requireFeatureFlag } from './guards/require-feature-flag';
import {
  launchBudget,
  hasStoredAuth0Session,
  shouldAdmitWhileAuthLoads,
} from '@/utils/launch-auth-budget';
import { composeBeforeEnter } from './compose-before-enter';
import { createBmtCheckInGuard } from './guards/bmt-check-in-guard';
import { MOBILE_PAYMENTS_FLAG } from '@/constants/Payment/payment-methods';
import {
  COURSES_E2EE_ROTATION_FLAG,
  COURSES_NOTES_WEB_FLAG,
  IDR_UNLOCK_FLAG,
  LEADER_DASHBOARD_QR_FLAG,
} from '@/constants/courses';
import { BUILDING_FUND_PLEDGE_PATH } from '@/constants/MyGiving/building-fund-pledge';
import { AGM_FLAG_OFF_PATH, AGM_ROUTES, AGM_RSVP_FLAG } from '@/constants/agm';
import {
  BMT_CHECK_IN_PATH,
  BMT_COMPANION_FLAG,
  BMT_HUB_PATH,
  BMT_RESOURCES_PATH,
  BMT_SCHEDULE_PATH,
  BMT_TASKS_PATH,
} from '@/constants/bmt';
import {
  agmStatusBar,
  campaignStatusBar,
  claimStatusBar,
  releaseStatusBar,
} from '@/utils/status-bar-claims';

// Pages that do not require authentication
const excludeGuardPaths = [
  '/',
  '/privacy-policy',
  '/terms-of-use',
  '/pre-sign-in',
  '/registration',
  '/success-launch',
  '/forgot-email',
  '/forgot-email/reset',
  '/forgot-email/contact-us',
  '/forgot-password',
  '/youtube',
  '/debug-force-update',
  '/vinyl-test',
  // The Training Portal gateway handles sign-in itself (web build only).
  TRAINING_GATEWAY_PATH,
  TRAINING_SIGNED_OUT_PATH,
];

function createPreviewGuard(to, from, next) {
  const id = to.params.id;
  const expectedId = process.env.VUE_APP_PREVIEW_ID;
  if (!expectedId || id !== expectedId) {
    next({ path: '/404' });
    return;
  }
  next();
}

// Blocks direct URL access and deeplinks into Courses for NF (N) / V pastoral status.
// Extracted to its own module (mirrors createMyGivingPinGuard) so the pastoral-sort
// logic is unit-testable without importing this whole router file.
const requireMinPastoralStatus = createCoursesPastoralGuard(store);

// Gate all Courses routes (program landings + lessons) behind the `courses` flag AND
// a minimum pastoral status, with an access token primed first. Delegates to
// requireFeatureFlag so the flag is loaded from the API (app-flags / user-flags)
// before deciding — same pattern as the scanner/vouchers routes — rather than
// reading a possibly-empty cached Map via isEnabled(). Kept as a single function
// (not an array) so the auth-guard composition below (`[authGuard, existing]`)
// doesn't end up nesting arrays. See courses-guard.js for why the order matters.
const coursesGuard = createCoursesGuard({
  ensureToken: () => ensureAccessToken('courses-guard'),
  requireFlag: () => requireFeatureFlag('courses'),
  requirePastoralStatus: requireMinPastoralStatus,
});

// Leader-only resources (banner/video/Life Stories) are reached via a QR that
// deep-links a lesson in leader view (`?view=leader`). This wraps coursesGuard
// and, only for a leader-view link, resolves leader access server-side — sending
// a non-leader to /courses/leader-only rather than a blank lesson. A single
// function (not an array), matching leaderDashboardGuard, so the auth-guard
// composition below doesn't nest arrays.
const leaderResourceGuard = createLeaderResourceGuard({ store, coursesGuard });

// The Notes Hub is a NATIVE feature (encryption spec, decision 7): the member key
// lives in the Keychain / Android backup, and a web build has nowhere safe to hold
// one. On web this sends the visitor back to the courses home unless the test-only
// `courses-notes-web` flag is on for them — the Playwright suite runs with it on
// against the dev API. Native never reads the flag. Wraps coursesGuard so the
// route keeps the same flag + pastoral gate as every other Courses route; a single
// function (not an array) so the auth-guard composition below doesn't nest arrays.
// See courses-notes-web-guard.js for why the web check runs first and why the
// flag is loaded through requireFeatureFlag rather than read from the Map.
const coursesNotesGuard = createCoursesNotesWebGuard({
  isNative: isNativeNotesPlatform,
  requireWebFlag: () => requireFeatureFlag(COURSES_NOTES_WEB_FLAG, NEXT_STEPS_CHOOSER_PATH),
  coursesGuard,
});

// The Leader's Dashboard carries its own flag on top of the Courses gate. Marking
// a lesson here writes a member_followup row and a Follow Up attendance record
// into ACTS — the church's system of record — so this needs to be switchable
// without an app release. The API gates the same `leader-dashboard` flag with
// @FeatureGated, which 404s when off; gating only one side would leave either a
// reachable endpoint or a screen that renders and then errors.
const leaderDashboardGuard = async to => {
  const flagResult = await requireFeatureFlag('leader-dashboard');
  if (flagResult) return flagResult;

  const coursesResult = await coursesGuard(to);
  if (coursesResult) return coursesResult;

  // canDisciple is a STRICTER gate than the leader-guide access that reveals the
  // entry card: leader-guide is granted to any pastoral-side leader at any level,
  // while discipling needs PCGL/POGL or above. Without this check a lower-ranked
  // leader reached a dashboard whose every call 403s — an error screen shown to
  // someone who is simply not eligible. Resolved server-side; the client never
  // decides it.
  const context = await store.dispatch('courses/fetchLeaderContext');
  if (context?.canDisciple) return undefined;

  // Two different noes. The server answering "not you" is a silent redirect —
  // there is nothing useful to say to someone who is not eligible. A request
  // that never got an answer (offline, 5xx) used to be treated identically, so
  // one flaky call threw an eligible leader back to Home with no idea why.
  if (context?.unavailable) {
    const toast = await toastController.create({
      message: 'Couldn’t check your leader access — try again in a moment.',
      duration: 3200,
      position: 'bottom',
      color: 'dark',
    });
    await toast.present();
  }
  return '/tabs/home';
};

// The unlock QR sits INSIDE the Leader's Dashboard but carries its own flag on top
// of it. `leader-dashboard` is already on in production, so it can only ever answer
// "yes" here — a QR that grants a member FULL access to a paid program for free
// needs a switch that is actually still un-thrown.
//
// Order matters: the QR flag is checked BEFORE the dashboard guard's own work. Both
// fall back to the same place, so the visible behaviour is identical either way, but
// checking the cheap flag first avoids fetchLeaderContext round-trips for a feature
// that is off. The dashboard guard still runs in full afterwards — this ADDS a gate,
// it does not replace canDisciple or the Courses gate.
const leaderQrGuard = async to => {
  const flagResult = await requireFeatureFlag(LEADER_DASHBOARD_QR_FLAG);
  if (flagResult) return flagResult;
  const dashboardResult = await leaderDashboardGuard(to);
  if (dashboardResult) return dashboardResult;
  // Dashboard access is not enough to hand out free access. The server says who
  // may mint (`canMintUnlock`: PCGL / POGL and above, or a pastor — owner
  // decision 2026-09-03), and a typed URL has to meet the same bar as the icon
  // the dashboard hides. The context was just fetched by the guard above.
  return store.state.courses.leaderContext?.canMintUnlock === false ? '/tabs/home' : undefined;
};

const routes = [
  {
    path: '/',
    // On the web build an Auth0 callback the Training gateway started goes back to it, past
    // /login and its onboarding/home redirects. See trainingCallbackRedirect.
    redirect: isNativeNotWeb() ? '/pre-sign-in' : to => trainingCallbackRedirect(to) || '/login',
  },
  {
    path: TRAINING_GATEWAY_PATH,
    component: () => import('../pages/training/index.vue'),
    meta: { hideMasterLayoutFooter: true },
  },
  {
    path: TRAINING_SIGNED_OUT_PATH,
    component: () => import('../pages/training/signed-out.vue'),
    meta: { hideMasterLayoutFooter: true },
  },
  {
    path: '/login',
    component: Login,
  },
  {
    path: '/pre-sign-in',
    component: PreSignIn,
  },
  {
    path: '/registration',
    component: Registration,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/register',
    component: Onboarding,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/easter-egg',
    component: EasterEggPage,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/photobooth-animation',
    component: () => import('../pages/photobooth-animation/index.vue'),
    meta: { disableSwipeNavigation: true },
  },
  // Y21 frontdoor (docs/y21-frontdoor.md). The preview shows both
  // homepage animations without a CMS entry; Recycle Toss opens from the
  // recycling bin in the Drop Room's rooms.
  {
    path: '/y21',
    component: () => import('../pages/y21/preview.vue'),
    beforeEnter: () => requireFeatureFlag(Y21_FLAG, '/tabs/home', { targeted: true }),
  },
  {
    path: '/y21/recycle-toss',
    component: () => import('../pages/y21/recycle-toss.vue'),
    beforeEnter: () => requireFeatureFlag(Y21_FLAG, '/tabs/home', { targeted: true }),
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/easter-egg/gallery',
    component: () => import('../pages/easter-egg/gallery.vue'),
  },
  {
    path: '/absolutely',
    component: AbsolutelyEntry,
  },
  {
    path: '/absolutely/onboarding',
    component: AbsolutelyOnboarding,
  },
  {
    path: '/absolutely/home',
    component: AbsolutelyHome,
  },
  {
    path: '/absolutely/chapter',
    component: ChapterIndex,
  },
  {
    path: '/absolutely/unlock',
    component: AbsolutelyUnlock,
  },
  {
    path: '/absolutely/chapter/:id',
    component: () => import('../pages/absolutely/chapter/_id.vue'),
  },
  {
    path: '/absolutely/chapter/:id/complete',
    component: () => import('../pages/absolutely/chapter/_id/complete.vue'),
  },
  {
    path: '/absolutely/parents-guide',
    component: () => import('../pages/absolutely/parents-guide.vue'),
  },
  {
    path: '/absolutely/chapter/:id/:stepId',
    component: () => import('../pages/absolutely/chapter/_id/_stepId.vue'),
  },
  // Courses routes — one shared landing per program at /courses/:programSlug
  // (programSlug comes from route params). The static /courses prefix keeps these
  // param routes non-greedy: all-static routes (e.g. /courses/notes) are declared
  // before /courses/:programSlug/:lessonId so a lesson slug can't shadow them.
  {
    // Bare /courses → the Next Steps part chooser (the All-page "Next Steps"
    // icon lands here). Each card opens a program landing below. Behind the
    // same coursesGuard as the rest of the surface.
    path: '/courses',
    component: () => import('../pages/courses/next-steps.vue'),
    beforeEnter: coursesGuard,
  },
  {
    // Cross-lesson notes & highlights hub. The static /courses/notes path
    // outranks the /courses/:programSlug param route below. Web-gated on top of
    // the Courses gate — see coursesNotesGuard above.
    path: '/courses/notes',
    component: () => import('../pages/courses/notes-hub/index.vue'),
    beforeEnter: coursesNotesGuard,
  },
  // The encryption screens of spec 6.7. Same static-before-param rule as
  // /courses/notes above, and the same guard: they are part of the notes
  // surface, so a web build without the test-only flag must not reach them
  // either — a Devices screen for a feature the build does not offer is a dead
  // end with a "Start over" button on it.
  //
  // The paths are written out rather than read from `ENCRYPTION_ROUTES`
  // (src/constants/courses-encryption.js) because two specs read THIS FILE as
  // text — private-regions.spec.js holds every /courses route to the
  // private-region mixin, and gated-routes.spec.js checks the guards — and
  // neither can follow a constant. That the two agree is pinned in
  // tests/unit/router/gated-routes.spec.js.
  {
    // Screen 5: the devices that can read the member's notes, and the recovery
    // code that gets them back in.
    path: '/courses/devices',
    component: () => import('../pages/courses/encryption/DevicesPage.vue'),
    beforeEnter: coursesNotesGuard,
  },
  {
    // Screen 1's destination: a code, shown once. Reached only from the Devices
    // screen and the Hub's recovery nudge, which is why it is not in the shared
    // ENCRYPTION_ROUTES map.
    path: '/courses/devices/recovery-code',
    component: () => import('../pages/courses/encryption/RecoveryCodePage.vue'),
    beforeEnter: coursesNotesGuard,
  },
  {
    // The optional passphrase (#1695): set, change, or — with `?mode=enter` —
    // typed on a device that holds no key. One route rather than three,
    // because set-versus-change is not the member's decision to make and the
    // page reads it off the status.
    path: '/courses/devices/passphrase',
    component: () => import('../pages/courses/encryption/PassphrasePage.vue'),
    beforeEnter: coursesNotesGuard,
  },
  {
    // ⚠️ Rotation (#1694, spec 6.12): a new key, every row re-sealed under it,
    // and every OTHER device signed out. Reached only from the Devices screen.
    path: '/courses/devices/sign-out-others',
    component: () => import('../pages/courses/encryption/SignOutOtherDevicesPage.vue'),
    // Two gates, not one. `coursesNotesGuard` is the notes surface; the second
    // is rotation's own flag, off everywhere until #1694's remaining defects
    // are fixed — the constant in `constants/courses.js` says what a member
    // risks without it. The fallback is the Devices screen they came from
    // rather than the home tab, so a member who somehow reaches the URL lands
    // somewhere that makes sense.
    beforeEnter: async to => {
      // ONE guard, not `beforeEnter: [a, b]`. The array form is valid Vue
      // Router, and the auth wrapper below now flattens it, but every other
      // route in this file composes by hand and a lone exception is the thing
      // that goes wrong quietly.
      const notes = await coursesNotesGuard(to);
      if (notes) return notes;
      return requireFeatureFlag(COURSES_E2EE_ROTATION_FLAG, '/courses/devices');
    },
  },
  {
    // Screen 4: the way back in on a device that holds no key.
    path: '/courses/devices/recover',
    component: () => import('../pages/courses/encryption/RecoverPage.vue'),
    beforeEnter: coursesNotesGuard,
  },
  {
    // "Approve this device" (encryption spec section 6.7, screen 2): where a
    // device that holds no master key waits for one the member already uses to
    // approve it. Reached by a redirect from the Notes Hub and by the button on
    // the reader's locked notice, both carrying `?from=` so the member is put
    // back where they were once the key arrives.
    //
    // Static, and declared before /courses/:programSlug/:lessonId so "devices"
    // is never read as a program slug. ENCRYPTION_ROUTES (constants/
    // courses-encryption.js) is where this path is named; #1547 and #1549
    // register the three siblings under the same /courses/devices prefix.
    //
    // The SAME guard as /courses/notes, and for the same reason: this screen
    // exists only to unlock notes, so a build where notes are unavailable has
    // nothing to send anybody here for.
    //
    // A LITERAL, not `ENCRYPTION_ROUTES.approveDevice`, even though that
    // constant is where this path is named. Three specs read THIS FILE as text
    // and would not see a constant: private-regions.spec.js, which WALKS every
    // declared route under /courses and holds each to the replay-privacy mixin;
    // gated-routes.spec.js, which is an allowlist this route has been added to
    // by hand (it walks nothing, so it reports nothing missing); and
    // encryption-routes.spec.js, which builds a router from the declared paths.
    // encryption-routes.spec.js also pins the literal and the constant together
    // so they cannot drift. (Do not write the matched pattern in a comment
    // either — the walk finds it there too, and reads the comment as a route
    // with no component.)
    path: '/courses/devices/approve',
    component: () => import('../pages/courses/encryption/ApproveThisDevicePage.vue'),
    beforeEnter: coursesNotesGuard,
  },
  {
    // "Start over" and "Reset pending" (encryption spec section 6.7, screens 6
    // and 7): erase the member's key bundle and every note, highlight, tag and
    // answer under it — immediately from a device that can prove it holds the
    // key, and after a seven-day hold from the member who has lost everything
    // (decision 16). ONE route for both screens, because a hold already
    // running is the ANSWER to "start over" rather than an error on the way to
    // it; the page picks the screen (StartOverPage.vue says why).
    //
    // Reached from the Start over link on the approval waiting screen, which
    // gates itself on this route being registered, and from the Devices screen.
    //
    // Static, and declared before /courses/:programSlug/:lessonId so "devices"
    // is never read as a program slug. ENCRYPTION_ROUTES (constants/
    // courses-encryption.js) is where this path is named.
    //
    // The SAME guard as /courses/notes and the approval screen, and for the
    // same reason: these screens exist only for a member who has notes, so a
    // build where notes are unavailable has nothing to send anybody here for.
    // The pastoral guard needs no change — `devices` is already classified
    // `gated` in its STATIC_SEGMENTS, which covers every path under the prefix.
    //
    // A LITERAL, not `ENCRYPTION_ROUTES.startOver`, for the reason the approval
    // route above gives at length: three specs read THIS FILE as text and would
    // not see a constant, and encryption-routes.spec.js pins the literal and
    // the constant together so they cannot drift.
    path: '/courses/devices/start-over',
    component: () => import('../pages/courses/encryption/StartOverPage.vue'),
    beforeEnter: coursesNotesGuard,
  },
  {
    // The redirect target for a non-leader who opens a leader-only resource
    // (see leaderResourceGuard). Static path, declared before the :programSlug
    // param route so "leader-only" isn't read as a program slug. Gated by the
    // plain coursesGuard — it must NOT require leader status (it is the page
    // shown to non-leaders).
    path: '/courses/leader-only',
    component: () => import('../pages/courses/leader-only.vue'),
    beforeEnter: coursesGuard,
  },
  {
    // Where a scanned resource QR lands. `deep_link_config.path` for a gated
    // resource points here, so the existing anonymous classify -> pending-deep-link
    // -> Auth0 -> resume flow is untouched and the access decision happens ONCE,
    // after sign-in, where a token exists. Teaching classifyDeepLink about auth
    // instead would put an API call in front of the token (ONE-7904's neighbour:
    // "click-through works, refresh logs me out").
    //
    // Static `resource` segment declared before /courses/:programSlug, same reason
    // as /courses/notes and /courses/leader-only above.
    path: '/courses/resource/:key',
    component: () => import('../pages/courses/resource/_key.vue'),
    beforeEnter: coursesGuard,
  },
  {
    // Where a scanned unlock card lands. Its own screen, not the scanner: cards
    // are read with the PHONE's camera, so the member arrives with no app screen
    // behind them, and routing them through a viewfinder while an invisible
    // redemption ran underneath was disorienting.
    //
    // Static `redeem` segment declared before /courses/:programSlug/:lessonId,
    // same reason as /courses/notes and /courses/resource above.
    //
    // Two gates, and deliberately NOT coursesGuard. The flag is what separates
    // deploying this from releasing it — merging to `development` queues the next
    // production release, so a new member-facing screen has to ship dormant.
    //
    // coursesGuard is wrong here for a different reason: it adds a pastoral-status
    // check, and a V or N holding a printed card is exactly who this lane exists
    // for. Bouncing them would be the dead end the CG block was designed to avoid.
    // Whether they may READ the course is still decided downstream.
    path: '/courses/redeem/:code',
    component: CourseRedeem,
    meta: { disableSwipeNavigation: true },
    beforeEnter: async () => {
      await ensureAccessToken('courses-redeem');
      return requireFeatureFlag(IDR_UNLOCK_FLAG);
    },
  },
  {
    // Where a scanned leader unlock QR lands. Auth only, and DELIBERATELY no
    // feature flag: the flag that gates this feature is `leader-dashboard`, which
    // is a LEADER capability — and the person arriving here is explicitly not a
    // leader. Gating the route on it would bounce exactly the members the QR is
    // for. The API decides, reading that flag as a global switch.
    path: '/courses/unlock/:token',
    component: CourseLeaderUnlock,
    meta: { disableSwipeNavigation: true },
    beforeEnter: async () => {
      await ensureAccessToken('courses-unlock');
    },
  },
  // Two gates, deliberately: the `leader-dashboard` flag decides whether the
  // feature exists at all, and canDisciple (PCGL/POGL and above, resolved
  // server-side) decides who it is for. A flag alone would expose it to every
  // member once switched on; a role check alone could not be turned off without
  // an app release.
  {
    path: '/courses/:programSlug/leader',
    component: () => import('../pages/courses/leader/index.vue'),
    beforeEnter: leaderDashboardGuard,
  },
  {
    // Consent/explainer shown before the QR itself — "Continue" is what advances
    // to /qr/code, and it is where the reason and the has-paid answer are given.
    //
    // leaderQrGuard, not leaderDashboardGuard: everything the dashboard requires,
    // PLUS the `leader-dashboard-qr` flag. The dedicated flag was retired on
    // 2026-09-01 and reinstated for the production cut — `leader-dashboard` is on in
    // production, so riding it would have shipped free full-access codes live on
    // deploy with no way to stop them short of taking the dashboard down.
    path: '/courses/:programSlug/leader/qr',
    component: () => import('../pages/courses/leader/QrConsent.vue'),
    beforeEnter: leaderQrGuard,
  },
  {
    // Gated in its own right, not merely reached through the consent screen: this
    // is the screen that renders a redeemable code, and route guards are the only
    // thing standing between a typed URL and it.
    path: '/courses/:programSlug/leader/qr/code',
    component: () => import('../pages/courses/leader/QrCode.vue'),
    beforeEnter: leaderQrGuard,
  },
  {
    path: '/courses/:programSlug',
    component: CoursesIndex,
    beforeEnter: coursesGuard,
  },
  {
    // A program's standalone leader resource ("Stories of HOGC"), opened from a card
    // under the lesson carousel. Declared BEFORE /courses/:programSlug/:lessonId so
    // the static `resource` segment isn't swallowed by the two-param lesson route.
    //
    // coursesGuard, not leaderResourceGuard: that one exists to resolve leader access
    // for `?view=leader` deep links into a LESSON, and there is no lesson here. Leader
    // access is settled server-side — the API 404s a member without it, and the screen
    // renders that as "Resource not found", which is the right answer to give someone
    // who should not learn that the resource exists.
    path: '/courses/:programSlug/resource/:resourceSlug',
    component: () => import('../pages/courses/_resourceSlug.vue'),
    beforeEnter: coursesGuard,
  },
  {
    // A single Life Story opened from the leader-only Life Stories resource.
    // Declared BEFORE /courses/:programSlug/:lessonId so the extra static `story`
    // segment isn't swallowed by the two-param lesson route.
    path: '/courses/:programSlug/:lessonId/story/:storyId',
    component: () => import('../pages/courses/_storyId.vue'),
    beforeEnter: leaderResourceGuard,
  },
  {
    path: '/courses/:programSlug/:lessonId',
    component: () => import('../pages/courses/_lessonId.vue'),
    beforeEnter: leaderResourceGuard,
  },
  {
    path: '/success-launch',
    component: SuccessLaunchPage,
  },
  // Library child routes (not part of tab redirect)
  {
    path: '/library/sermon/:slug',
    component: Sermon,
    name: 'sermon-detail',
    meta: { hideMasterLayoutFooter: true },
  },
  {
    path: '/library/search',
    component: SearchPage,
    name: 'library-search',
    meta: { hideMasterLayoutFooter: true },
  },
  {
    path: '/tabs',
    component: TabsPage,
    meta: { disableSwipeNavigation: true },
    children: [
      {
        path: '',
        redirect: '/tabs/home',
      },
      {
        path: 'home',
        component: () => import('../pages/tabs/HomePage.vue'),
      },
      {
        path: 'library',
        component: () => import('../pages/tabs/LibraryPage.vue'),
      },
      {
        path: 'all',
        component: () => import('../pages/tabs/AllPage.vue'),
      },
      {
        path: 'me',
        component: () => import('../pages/tabs/MePage.vue'),
      },
    ],
  },
  {
    path: '/home-debug',
    component: HomepageDebug,
  },
  {
    path: '/debug-force-update',
    component: () => import('../pages/debug-force-update/index.vue'),
  },
  {
    path: '/swipe-test',
    component: () => import('../components/SwipeNavigationTest.vue'),
  },
  {
    path: '/vinyl-test',
    component: () => import('../pages/vinyl-test/index.vue'),
  },
  {
    path: '/pre-loading',
    component: PreLoadingPage,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/profile',
    component: Profile,
  },
  {
    path: '/my-payments',
    component: () => import('../pages/my-payments/index.vue'),
    // Gated by the `mobile-payments` flag (default OFF until enabled in admin).
    beforeEnter: () => requireFeatureFlag(MOBILE_PAYMENTS_FLAG, '/tabs/me'),
  },
  {
    path: '/payment-details/:reference',
    component: () => import('../pages/payment-details/index.vue'),
    beforeEnter: () => requireFeatureFlag(MOBILE_PAYMENTS_FLAG, '/tabs/me'),
  },
  // The AGM RSVP and the EM briefing, behind the `agm-rsvp` flag (off in production until
  // the year's RSVP opens). The API gates the same flag, so a link with the flag off
  // lands on home. Authentication comes first: the loop below puts the auth guard in
  // front of this one, as it does for every other flagged route.
  {
    path: AGM_ROUTES.rsvp,
    component: () => import('../pages/agm/index.vue'),
    beforeEnter: () => requireFeatureFlag(AGM_RSVP_FLAG, AGM_FLAG_OFF_PATH),
  },
  {
    path: AGM_ROUTES.briefing,
    component: () => import('../pages/agm/briefing.vue'),
    beforeEnter: () => requireFeatureFlag(AGM_RSVP_FLAG, AGM_FLAG_OFF_PATH),
  },
  // Dev-only payment module demo harness (mocked backend). Excluded from prod builds.
  ...(process.env.VUE_APP_NODE_ENV !== 'production'
    ? [
        {
          path: '/payment-demo',
          component: () => import('../pages/payment-demo/index.vue'),
        },
        // Dev-only state switcher — drives the PaymentSheet straight into any
        // step (method / pin / processing / success / pending / failed) so each
        // screen can be verified without walking the whole flow.
        {
          path: '/payment-states',
          component: () => import('../pages/payment-states/index.vue'),
        },
        // Dev-only Next Steps Mock Mode console — lets a PM click through every
        // Next Steps access/unlock/payment/voucher flow with no backend. See
        // src/utils/next-steps-mock.js for the state machine it drives.
        {
          path: '/next-steps-mock',
          component: () => import('../pages/next-steps-mock/index.vue'),
        },
        // Dev-only voucher wallet state harness — seeds the real My Vouchers
        // screens with fixtures so every card and detail branch (partner pass,
        // course unlock, expired, cancelled) can be checked without
        // a backend. See src/utils/voucher-harness.js.
        {
          path: '/voucher-states',
          component: () => import('../pages/voucher-states/index.vue'),
        },
        // Dev-only linked-bank-account (eGIRO) state harness — seeds the real
        // Manage · Accounts screen with fixture rows so every mandate state can
        // be checked without a bank. See src/utils/egiro-harness.js.
        {
          path: '/egiro-states',
          component: () => import('../pages/egiro-states/index.vue'),
        },
        // Dev-only BF2026 pledge harness — a fake giving-api that speaks the
        // contract agreed for the campaign, so every pledge state can be reached
        // before giving-api supports it. See src/utils/bf-pledge-harness.js.
        {
          path: '/bf-pledge-states',
          component: () => import('../pages/bf-pledge-states/index.vue'),
        },
        // Dev-only AGM RSVP harness — a fake /v2/agm, so every state of the RSVP page (open,
        // answered, closed, ended, not eligible, could not load) can be reached without an
        // EM member or a CMS entry. See src/utils/agm-harness.js.
        {
          path: AGM_ROUTES.harness,
          component: () => import('../pages/agm-states/index.vue'),
        },
        // Dev-only form share links harness — fakes the two form-share routes and
        // stands in for a leader account and a form opted in with Strapi's
        // leadersCanShare. See src/utils/form-share-harness.js.
        {
          path: '/form-share-states',
          component: () => import('../pages/form-share-states/index.vue'),
        },
        // Hub listing every dev console, reachable from the Me page so the
        // harnesses do not need a deep link to find.
        {
          path: '/product-purchase',
          component: () => import('../pages/product-purchase/index.vue'),
        },
        // Dev-only text-size harness — the member-facing control plus a
        // stress slider that runs past the app's own ceiling, so layouts can be
        // checked at the accessibility sizes iOS can actually hand us.
        {
          path: '/font-scale',
          component: () => import('../pages/font-scale/index.vue'),
        },
        {
          path: '/dev-tools',
          component: () => import('../pages/dev-tools/index.vue'),
        },
        // Dev-only reminder-subscription harness — calls the admin tool's real
        // member endpoints as the signed-in member and shows this device's
        // OneSignal identity, so a daily reminder can be tested end to end
        // before the member-facing screen exists.
        {
          path: '/subscription-reminders',
          component: () => import('../pages/subscription-reminders/index.vue'),
        },
        // Dev-only lesson rich-text harness — mounts the real
        // LessonContentRenderer with fixture blocks so inline marks and the list
        // block can be checked in both themes and at the accessibility font
        // scales, with no lesson, no login and no seeded database.
        {
          path: '/lesson-blocks',
          component: () => import('../pages/lesson-blocks/index.vue'),
        },
        // Dev-only BMT Companion console — the real BMT home card and hub
        // against fixtures (not signed up, pending, approved, homework), with no
        // backend. See src/utils/bmt-harness.js.
        {
          path: '/bmt-mock',
          component: () => import('../pages/bmt-mock/index.vue'),
        },
      ]
    : []),
  {
    // BMT Companion: a trainee's sign-up, dates, check-in state and homework.
    // The API 404s every BMT route while the flag is off; this keeps the screen
    // out of reach too.
    path: BMT_HUB_PATH,
    component: () => import('../pages/bmt/index.vue'),
    beforeEnter: () => requireFeatureFlag(BMT_COMPANION_FLAG),
  },
  // The pages a hub tile opens. Same flag as /bmt.
  {
    path: BMT_SCHEDULE_PATH,
    component: () => import('../pages/bmt/schedule.vue'),
    beforeEnter: () => requireFeatureFlag(BMT_COMPANION_FLAG),
  },
  {
    path: BMT_RESOURCES_PATH,
    component: () => import('../pages/bmt/resources.vue'),
    beforeEnter: () => requireFeatureFlag(BMT_COMPANION_FLAG),
  },
  {
    path: BMT_TASKS_PATH,
    component: () => import('../pages/bmt/tasks.vue'),
    beforeEnter: () => requireFeatureFlag(BMT_COMPANION_FLAG),
  },
  {
    path: `${BMT_TASKS_PATH}/:id(\\d+)`,
    component: () => import('../pages/bmt/task.vue'),
    beforeEnter: () => requireFeatureFlag(BMT_COMPANION_FLAG),
  },
  {
    // Where a BMT check-in scan lands (utils/qr-handler.js). Same flag as /bmt;
    // with no scan result to show there is nothing here, so it goes to the hub.
    path: BMT_CHECK_IN_PATH,
    component: () => import('../pages/bmt/check-in.vue'),
    beforeEnter: createBmtCheckInGuard({
      requireFlag: () => requireFeatureFlag(BMT_COMPANION_FLAG),
      store,
    }),
  },
  {
    path: '/calendar',
    component: Calendar,
  },
  {
    path: '/photobooth',
    component: Photobooth,
  },
  {
    // Full-screen webview embed of the standalone prayer community service.
    // Gated behind the `prayer-community` flag (off by default → invisible)
    // so the embed can be piloted per-user before a wider rollout.
    path: '/prayer',
    component: () => import('../pages/prayer/index.vue'),
    meta: { disableSwipeNavigation: true },
    beforeEnter: () => requireFeatureFlag('prayer-community'),
  },
  {
    path: '/photodrop',
    component: Photodrop,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/photodrop/upload-test',
    component: PhotodropUploadTest,
  },
  {
    path: '/photographer-app',
    component: PhotographerApp,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/receive-photos',
    component: ReceivePhotos,
  },
  {
    path: '/change-password',
    component: ChangePassword,
  },
  {
    path: '/my-qr',
    component: MyQR,
  },
  {
    path: '/scanner',
    component: Scanner,
    meta: { disableSwipeNavigation: true },
    beforeEnter: to => {
      // Course unlock links are scanned with the PHONE's camera, never the in-app
      // scanner, so the lane has to keep working with the `scanner` flag off —
      // that is an explicit requirement of the feature, and the flag is off for
      // plenty of members. These links only pass through this route because
      // /scan/:type/:action/:ref redirects everything here; the page dispatches
      // them and navigates away without ever opening the camera.
      //
      // 'unlock' is carved out alongside 'redeem' for exactly the same reason,
      // and it closes a latent hole in the class-QR lane that predates the leader
      // QR: a member holding a class code who happened to have `scanner` off was
      // bounced to /my-qr and never unlocked anything. The leader QR would have
      // inherited that — a leader watching a member scan their screen and land on
      // their own membership card.
      // `leader-unlock` is listed even though classifyDeepLink and the /scan/
      // redirect both send it to /courses/unlock and it should never arrive here.
      // It DID arrive here — the branch existed only in the router redirect, which
      // a camera scan bypasses — and the punishment was a bounce to /my-qr for
      // every member whose `scanner` flag is off. If it ever reaches this route
      // again, landing on the scanner is a bad outcome; being thrown to a
      // membership card is a baffling one.
      // BMT check-in is carved out the same way: see scannerFlagFor.
      const flag = scannerFlagFor(to.query);
      if (!flag) return;
      return requireFeatureFlag(flag, '/my-qr');
    },
  },
  {
    path: '/scan/:type/:action/:ref(.*)*',
    redirect: to => {
      const ref = Array.isArray(to.params.ref) ? to.params.ref.join('/') : to.params.ref;
      // Unlock cards get the dedicated page above. Everything else still goes
      // through the scanner, which handles the other QR lanes.
      if (to.params.type === 'course' && to.params.action === 'redeem' && ref) {
        return {
          path: `/courses/redeem/${encodeURIComponent(ref)}`,
          query: to.query,
          hash: to.hash,
        };
      }

      // A leader's unlock QR. Its own action and its own page, for the same reason
      // redeem has one: this is scanned with the phone's CAMERA, so routing it
      // through /scanner showed the member a camera UI they never asked for while
      // the grant ran underneath — and left an entry in history that re-ran the
      // grant on Back.
      //
      // `ref` IS the token here. The action is `leader-unlock` rather than `unlock`
      // precisely so this stays unambiguous: `unlock` belongs to the class QR,
      // whose ref is a program slug.
      if (to.params.type === 'course' && to.params.action === 'leader-unlock' && ref) {
        return {
          path: `/courses/unlock/${encodeURIComponent(ref)}`,
          query: to.query,
          hash: to.hash,
        };
      }
      return {
        path: '/scanner',
        query: {
          type: to.params.type,
          action: to.params.action,
          ref,
          ...to.query,
        },
        hash: to.hash,
      };
    },
  },
  {
    path: '/vouchers',
    component: Vouchers,
    beforeEnter: () => requireFeatureFlag('vouchers'),
  },
  {
    path: '/vouchers/:id',
    component: VoucherDetail,
    beforeEnter: () => requireFeatureFlag('vouchers'),
  },
  {
    // Legacy deep-link shape — redirect to query form so the page always
    // mounts on `/service-replay` and back-stack behaviour stays clean.
    path: '/service-replay/:id',
    redirect: to => ({ path: '/service-replay', query: { replay: to.params.id } }),
  },
  {
    path: '/birthdayboard',
    component: BirthdayBoard,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/birthdayboard/message',
    component: BirthdayBoardMessage,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/birthdayboard/favourite-messages',
    component: BirthdayBoardFavouriteMessages,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/birthdayboard/hidden-messages',
    component: BirthdayBoardHiddenMessages,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/birthdayboard/my-messages',
    component: BirthdayBoardMyMessages,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/birthdayboard/pinned-messages',
    component: BirthdayBoardPinnedMessages,
    meta: { disableSwipeNavigation: true },
  },
  {
    path: '/protected/:name',
    component: ProtectedPage,
  },
  {
    path: '/e-invites',
    component: EInvitesPage,
  },
  {
    path: '/milestones',
    component: Milestones,
  },
  {
    path: '/service-highlights',
    component: ServiceHighlight,
  },
  {
    path: '/updates',
    component: Updates,
  },
  {
    path: '/updates/:id',
    component: UpdateDetails,
  },
  {
    path: '/event-page/:slug',
    component: EventPage,
  },
  {
    path: '/md2023',
    component: MD2023,
  },
  {
    path: '/md2024-1',
    component: MD20241,
  },
  {
    path: '/md2024-2',
    component: MD20242,
  },
  {
    path: '/md2024-3',
    component: MD20243,
  },
  {
    path: '/md2024-4',
    component: MD20244,
  },
  {
    path: '/md2024-5',
    component: MD20245,
  },
  {
    path: '/hogcfd',
    component: FathersDay,
  },
  {
    path: '/hogcfd/2013',
    component: FD2013,
  },
  {
    path: '/hogcfd/2014',
    component: FD2014,
  },
  {
    path: '/hogcfd/2015',
    component: FD2015,
  },
  {
    path: '/hogcfd/2016',
    component: FD2016,
  },
  {
    path: '/hogcfd/2017',
    component: FD2017,
  },
  {
    path: '/hogcfd/2024',
    component: FD2024,
  },
  {
    path: '/church-updates',
    component: ChurchUpdates,
  },
  {
    path: '/church-updates/:slug',
    component: ChurchUpdatesReadMore,
  },
  // Preview routes (no Auth0 required)
  // ID must match VUE_APP_PREVIEW_ID environment variable
  // Only these routes are allowed: /preview/:id/home, /preview/:id/church-updates, /preview/:id/church-updates/:slug
  {
    path: '/preview/:id',
    redirect: to => {
      const id = to.params.id;
      const expectedId = process.env.VUE_APP_PREVIEW_ID;
      // If ID matches, redirect to home, otherwise 404
      if (expectedId && id === expectedId) {
        return `/preview/${id}/home`;
      }
      return '/404';
    },
  },
  {
    path: '/preview/:id/home',
    component: () => import('../pages/preview/home.vue'),
    props: route => ({
      id: route.params.id,
    }),
    beforeEnter: createPreviewGuard,
  },
  {
    path: '/preview/:id/church-updates',
    component: () => import('../pages/preview/church-updates/index.vue'),
    props: route => ({
      id: route.params.id,
    }),
    beforeEnter: createPreviewGuard,
  },
  {
    path: '/preview/:id/church-updates/:slug',
    component: () => import('../pages/preview/church-updates/_slug.vue'),
    props: route => ({
      id: route.params.id,
      slug: route.params.slug,
    }),
    beforeEnter: createPreviewGuard,
  },
  {
    path: '/preview/:id/event-page/:slug',
    component: () => import('../pages/preview/event-page/_slug.vue'),
    props: route => ({
      id: route.params.id,
      slug: route.params.slug,
    }),
    beforeEnter: createPreviewGuard,
  },
  // Catch-all for invalid preview routes - redirect to 404
  {
    path: '/preview/:id/:pathMatch(.*)*',
    beforeEnter: (to, from, next) => {
      const id = to.params.id;
      const expectedId = process.env.VUE_APP_PREVIEW_ID;
      if (expectedId && id === expectedId) {
        const path = to.path;
        const allowedPaths = [`/preview/${id}/home`, `/preview/${id}/church-updates`];
        const isChurchUpdatesSlug = path.match(
          new RegExp(
            `^/preview/${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/church-updates/[^/]+$`,
          ),
        );
        const isEventPageSlug = path.match(
          new RegExp(`^/preview/${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/event-page/[^/]+$`),
        );

        if (allowedPaths.includes(path) || isChurchUpdatesSlug || isEventPageSlug) {
          // This shouldn't happen as specific routes should match first, but just in case
          next();
          return;
        }
      }
      next({ path: '/404' });
    },
  },
  // Redirect old tab routes to new tab structure
  {
    path: '/home',
    redirect: '/tabs/home',
  },
  {
    path: '/library',
    redirect: '/tabs/library',
  },
  {
    path: '/allpage',
    redirect: '/tabs/all',
  },
  {
    path: '/mepage',
    redirect: '/tabs/me',
  },
  {
    path: '/forms/:formName',
    component: Forms,
  },
  {
    path: '/service-replay',
    component: ServiceReplayDetail,
  },
  // MyGiving routes are extracted to ./mygiving-routes.js and spread below
  ...createMyGivingRoutes(store),
  // Redirect old /bo-redirection to new path under /my-giving/
  {
    path: '/bo-redirection',
    redirect: '/my-giving/bo-redirection',
  },
  {
    path: '/forgot-email',
    component: ForgotEmail,
  },
  {
    path: '/forgot-email/reset',
    component: ForgotEmailReset,
  },
  {
    path: '/forgot-email/contact-us',
    component: ForgotEmailContactUs,
  },
  {
    path: '/forgot-password',
    component: ForgotPassword,
  },
  {
    path: '/admin/feature-flags',
    component: () => import('../pages/admin/feature-flags.vue'),
    beforeEnter: () => {
      const { isEnabled } = useFeatureFlags();
      if (!isEnabled('feature-flag-admin')) return '/tabs/home';
    },
  },
  {
    // Development-only in practice: the API answers 404 anywhere else, and the
    // screen says so. Gated on the same flag as the flags screen, so one
    // administrator can retarget `courses-e2ee` and clean up after it.
    //
    // Through `requireFeatureFlag`, not a bare `isEnabled`: this flag is
    // targeted at named people, so it reaches the client in USER flags, and a
    // cold cache would bounce the very administrator it is meant to admit.
    // The neighbouring flags route predates the helper.
    path: '/admin/encryption',
    component: () => import('../pages/admin/encryption.vue'),
    beforeEnter: () => requireFeatureFlag('feature-flag-admin'),
  },
  {
    path: '/youtube',
    component: YoutubeProxy,
  },
  {
    // External sister apps open this route through a web URL or native deep link.
    // The route itself is not enough to approve; the native auth guard below still
    // requires an authenticated HOGC session before the page can fetch details.
    //
    // Behind `sso-broker`, the same flag the API gates every /v2/sso route with.
    // Without it the page rendered and then failed its fetch with a 404 once the
    // broker went dark, which reads as a broken approval rather than a feature
    // that is not on yet. The flag name must stay in step with SSO_BROKER_FLAG in
    // hogc-one-api's sso.controller.ts. Safe to resolve pre-auth: /app-flags is
    // public, so the guard can decide before the member signs in.
    path: '/sso/approve/:requestId',
    component: SsoApprove,
    beforeEnter: () => requireFeatureFlag('sso-broker'),
    meta: { hideMasterLayoutFooter: true },
  },
  {
    // Behind the same flag as approval: without the broker there are no sessions to
    // manage, so the screen would only ever be empty.
    path: '/connected-apps',
    component: SsoConnectedApps,
    beforeEnter: () => requireFeatureFlag('sso-broker'),
    meta: { hideMasterLayoutFooter: true },
  },
];

routes.forEach(route => {
  // Web-only Auth0 guard — skip for preview routes and excluded paths
  if (
    !excludeGuardPaths.includes(route.path) &&
    !route.path.startsWith('/preview/') &&
    process.env.VUE_APP_NODE_ENV !== 'local' &&
    !isNativeNotWeb()
  ) {
    const authGuard = createAuthGuard({
      redirectLoginOptions: {
        appState: {
          returnTo: window.location.pathname,
        },
      },
    });
    // Auth first, then whatever the route already declared. See the file it
    // lives in for why this is not written inline.
    route.beforeEnter = composeBeforeEnter(authGuard, route.beforeEnter);
  }
});

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Reader text size is a Next Steps setting, not an app-wide one. Applying it
// here rather than in the courses pages means there is exactly one place that
// decides, and leaving the section always resets — a member who set Largest to
// read a lesson does not then find the giving flow at 150%.
router.afterEach(to => {
  applyScaleForRoute(to.path);
});

// A route is reachable signed-out if it is on the no-auth-guard list, is a
// Storyblok preview route, or is the login screen itself. Everything else needs a
// token in the store before its guards run.
const isPublicPath = path =>
  excludeGuardPaths.includes(path) ||
  String(path || '').startsWith('/preview/') ||
  path === '/login';

// The first navigation of a launch — the one whose guards run inside
// router.isReady(), before anything is on screen.
const isInitialNavigation = from => from === START_LOCATION;

// Before everything else: a visitor on the Training gateway is never taken into the app.
router.beforeEach(keepOnTrainingGateway);

// Registered first, and before any route's beforeEnter by definition, so every
// authenticated route starts with a usable token. See token-priming-guard.js.
router.beforeEach(
  createTokenPrimingGuard({
    ensureToken: () => ensureAccessToken('router-priming'),
    isPublicPath,
    isInitialNavigation,
    launchBudget,
  }),
);

// After token priming, so the profile it may wait for can load. See the file.
router.beforeEach(createMyGivingLinkGuard(store));

router.beforeEach((_to, from) => {
  localStorage.setItem('route:previous', from.fullPath);
});

// Force replace (not push) when navigating to tab routes to avoid stacking history
const TAB_PATHS = [
  '/tabs/home',
  '/tabs/library',
  '/tabs/all',
  '/tabs/me',
  '/home',
  '/library',
  '/allpage',
  '/mepage',
];
const originalPush = router.push.bind(router);
router.push = function (to, ...args) {
  const path = typeof to === 'string' ? to : to?.path;
  if (path && TAB_PATHS.includes(path)) {
    return router.replace(to);
  }
  return originalPush(to, ...args);
};

// Track all matched routes regardless of auth state. Anonymous web users can
// browse most of the app, and autocapture ($mp_web_page_view) already records
// them — gating the semantic Page Viewed on auth0.isAuthenticated made the
// two signals diverge and hid the anonymous cohort from route-level analysis.
// Sensitive routes are still excluded inside trackAnalyticsEvent via
// shouldBlockTracking, and preview routes carry no user traffic worth losing.
const shouldTrackRouterPageView = to => !(Array.isArray(to?.matched) && to.matched.length === 0);

router.afterEach(to => {
  markTabRouteReady(to.path);
});

// Going home ends the MyGiving PIN session (VAPT step-up). Not on app pause and
// not on a timer while the member is in the middle of a transaction.
router.afterEach(createEndPinSessionOnHome(store));

// The BF2026 pages run their navy band up under the status bar. Claimed here,
// once, rather than in each page's hooks: Ionic fires the entering page's
// ionViewWillEnter before the leaving page's ionViewWillLeave.
router.afterEach(to => {
  if (to.path.startsWith(BUILDING_FUND_PLEDGE_PATH))
    claimStatusBar('bf-pledge', campaignStatusBar());
  else releaseStatusBar('bf-pledge');
});

// The AGM RSVP's dark event header runs up under the status bar, so its icons need to
// be light. Only /agm: the briefing is an ordinary light page.
router.afterEach(to => {
  if (/^\/agm\/?$/.test(to.path)) claimStatusBar('agm', agmStatusBar());
  else releaseStatusBar('agm');
});

router.beforeEach(to => {
  const rawPath =
    typeof window !== 'undefined' && window.location?.pathname ? window.location.pathname : to.path;

  // Storyblok Visual Editor passes ?path=... when opening preview routes.
  // Redirect to the specific preview article when path is provided.
  // Extract id from current route if already in preview
  if (rawPath?.startsWith('/preview')) {
    const expectedId = process.env.VUE_APP_PREVIEW_ID;
    const currentId =
      to.params.id ||
      (expectedId &&
        rawPath.match(
          new RegExp(`^/preview/(${expectedId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`),
        ))?.[1];
    const searchPath =
      (typeof to.query?.path === 'string' && to.query.path) ||
      (typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('path')
        : null);

    if (searchPath && currentId) {
      const normalizedPath = searchPath.replace(/^\/+/, '');
      if (normalizedPath) {
        // Preserve id in the target path
        const targetPath = `/preview/${currentId}/${normalizedPath}`.replace(/\/{2,}/g, '/');
        if (targetPath !== to.path) {
          const { path: _ignoredPathParam, ...restQuery } = to.query || {};

          // Preserve Storyblok parameters from current URL
          // These parameters are crucial for the Bridge to function correctly in Visual Editor
          try {
            const storyblokParams = getStoryblokUrlParams();
            Object.assign(restQuery, storyblokParams);
          } catch (error) {
            console.warn('[Router] Error preserving Storyblok params:', error);
          }

          return {
            path: targetPath,
            query: restQuery,
            hash: to.hash,
            replace: true,
          };
        }
      }
    }
  }
});

router.beforeEach(async (to, from) => {
  // Preview routes are excluded from Auth0 guards
  if (to.path.startsWith('/preview/')) {
    return; // Allow preview routes without authentication
  }

  if (process.env.VUE_APP_NODE_ENV === 'local') {
    if (to.path === '/scanner') return;
    // The lesson rich-text harness renders fixture blocks and calls nothing, so a
    // login would only stand between a developer and the thing they came to look
    // at. Same exemption as /scanner above; the route itself is stripped from
    // production builds.
    if (to.path === '/lesson-blocks') return;
    // The BMT console serves fixtures and calls nothing either.
    if (to.path === '/bmt-mock') return;
    if (!localStorage.getItem('token') && to.path !== '/login') return { path: '/login' };
    return;
  }

  if (
    !excludeGuardPaths.includes(to.path) &&
    process.env.VUE_APP_NODE_ENV !== 'local' &&
    to.path !== '/login' &&
    to.path !== '/pre-sign-in' &&
    isNativeNotWeb()
  ) {
    if (!auth0.isAuthenticated.value) {
      // A launch whose token priming ran out of budget (see token-priming-guard)
      // reaches here with Auth0 still checking the stored session, so
      // `isAuthenticated` is false only because the answer is not in yet. Sending
      // that member to /login would trade a white screen for a wrong one; let the
      // first navigation through instead. main.js sends them to /login if the
      // session turns out to have ended.
      if (
        shouldAdmitWhileAuthLoads({
          isInitialNavigation: isInitialNavigation(from),
          isLoading: auth0.isLoading?.value === true,
          hasStoredSession: hasStoredAuth0Session(),
        })
      ) {
        return;
      }
      // For SSO, show the branded pre-sign-in choice first. Sending users straight
      // to Auth0 from a sister app is jarring, and pre-sign-in now preserves the
      // pending approval route through Auth0 appState.
      return { path: to.path.startsWith('/sso/approve/') ? '/pre-sign-in' : '/login' };
    }
  }
});

// Block tracking for /my-giving/* routes
router.afterEach((to, from) => {
  // Block Mixpanel tracking and recording for /my-giving/* routes
  if (shouldBlockTracking(to.path)) {
    // Opt out of Mixpanel event tracking when on /my-giving/* routes
    // Only call if Mixpanel is initialized (has config)
    if (mixpanelInstance && mixpanelInstance.config && mixpanelInstance.opt_out_tracking) {
      try {
        // opt_out_tracking defaults to delete_user: true + clear_persistence: true,
        // which deletes the Mixpanel people profile server-side and wipes the
        // local distinct_id. We only want to pause event tracking on sensitive
        // routes — never destroy the user's identity or profile.
        mixpanelInstance.opt_out_tracking({ delete_user: false, clear_persistence: false });
        console.log('[Mixpanel] Event tracking disabled for /my-giving/* routes');
      } catch (error) {
        console.error('[Mixpanel] Error opting out of tracking:', error);
      }
    }

    // Stop Mixpanel session recording
    stopMixpanelRecording();

    // Stop Datadog RUM session replay recording for sensitive routes
    handleRouteChangeForSessionReplay(to.path);
  } else {
    // Re-enable Mixpanel tracking when leaving /my-giving/* routes
    // Only call if Mixpanel is initialized (has config)
    if (mixpanelInstance && mixpanelInstance.config && mixpanelInstance.opt_in_tracking) {
      try {
        // Only opt back in if we previously opted out — opt_in_tracking fires a
        // $opt_in event every call, so running it on every navigation spams the
        // project with junk events.
        if (
          typeof mixpanelInstance.has_opted_out_tracking === 'function' &&
          mixpanelInstance.has_opted_out_tracking()
        ) {
          mixpanelInstance.opt_in_tracking();
          console.log('[Mixpanel] Event tracking re-enabled');
        }
      } catch (error) {
        console.error('[Mixpanel] Error opting in to tracking:', error);
      }
    }

    // Resume Mixpanel session recording
    startMixpanelRecording();

    // Start Datadog RUM session replay recording for non-sensitive routes
    handleRouteChangeForSessionReplay(to.path);
  }

  if (shouldTrackRouterPageView(to)) {
    trackPageView(to, from);
  }
});

// Ensure orientation is correctly set after navigation completes
// Use setTimeout to ensure this runs after component lifecycle hooks (mounted)
router.afterEach(to => {
  // Skip orientation locking on Android (plugin not working)
  // Only handle on iOS native platforms
  const isIos = isPlatform('ios') && !isPlatform('mobileweb');
  if (!isIos) {
    return;
  }

  // Delay execution to ensure component lifecycle hooks (mounted) have completed
  // This acts as a safety net in case component lifecycle hooks don't fire correctly
  setTimeout(async () => {
    const action = getOrientationActionForRoute(to.path);

    if (action === 'landscape') {
      await lockOrientation('landscape');
    } else if (action === 'portrait') {
      await lockOrientation('portrait');
    } else if (action === 'unlock') {
      await unlockOrientation();
    }
  }, 100);
});

// A one-shot `if (auth0?.isAuthenticated?.value)` token fetch used to sit here. It
// read like the app-wide safety net and was not one: this module is imported by
// main.js (line 3) before main.js installs the Auth0 plugin, so the SDK had not
// restored the session yet and `isAuthenticated` was false every time — the body
// never ran on a cold load. Priming now happens per-navigation in the beforeEach
// above, which is the only point that is guaranteed to be after the SDK is ready
// and before a guard can call the API. Its terminal-error logout is not lost:
// main.js runs the identical `login_required` / `invalid_grant` /
// `missing_refresh_token` handling on the launch token fetch.

export default router;
